<script setup lang="ts">
  import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
  } from "@/components/ui/alert-dialog";
  import { Button } from "@/components/ui/button";
  import { Spinner } from "@/components/ui/spinner";

  const props = withDefaults(
    defineProps<{
      title: string;
      description?: string;
      confirmLabel?: string;
      cancelLabel?: string;
      variant?: "destructive" | "default";
      loading?: boolean;
      autoClose?: boolean;
    }>(),
    {
      description: undefined,
      confirmLabel: "Confirm",
      cancelLabel: "Cancel",
      variant: "destructive",
      loading: false,
      autoClose: true,
    },
  );

  const emit = defineEmits<{
    confirm: [];
    cancel: [];
  }>();

  const open = defineModel<boolean>("open", { default: false });

  const handleConfirm = () => {
    emit("confirm");
    if (props.autoClose) open.value = false;
  };

  const handleCancel = () => {
    emit("cancel");
    open.value = false;
  };
</script>

<template>
  <AlertDialog v-model:open="open">
    <AlertDialogTrigger
      v-if="$slots.trigger"
      as-child>
      <slot name="trigger" />
    </AlertDialogTrigger>
    <AlertDialogContent class="font-sans">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ title }}</AlertDialogTitle>
        <AlertDialogDescription v-if="description || $slots.default">
          <slot>{{ description }}</slot>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel
          :disabled="loading"
          @click="handleCancel">
          {{ cancelLabel }}
        </AlertDialogCancel>
        <Button
          :variant="variant"
          :disabled="loading"
          @click="handleConfirm">
          <Spinner v-if="loading" />
          {{ confirmLabel }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
